/**
 * Per-tab request meter (burst-guard): counts this tab's hub HTTP requests and encrypted report
 * list/read calls so You › Machines can show the cost of keeping the screens fresh. Memory only;
 * nothing is sent anywhere.
 */
export type MeterKind = 'http' | 'report.list' | 'report.read'
export interface MeterStats {
  /** Requests in the trailing 60 seconds. */
  lastMinute: number
  /** Requests in the trailing hour. */
  lastHour: number
  /** Most requests in any one clock minute during the trailing hour. */
  peakPerMinute: number
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE

class Series {
  /** Timestamps of the trailing minute (exact trailing count). */
  private recent: number[] = []
  /** Clock-minute buckets of the trailing hour. */
  private buckets = new Map<number, number>()
  record(at: number) {
    this.recent.push(at)
    const m = Math.floor(at / MINUTE)
    this.buckets.set(m, (this.buckets.get(m) ?? 0) + 1)
    this.prune(at)
  }
  private prune(now: number) {
    let i = 0
    while (i < this.recent.length && (this.recent[i] as number) <= now - MINUTE) i++
    if (i) this.recent.splice(0, i)
    const oldest = Math.floor((now - HOUR) / MINUTE)
    for (const m of this.buckets.keys()) if (m <= oldest) this.buckets.delete(m)
  }
  stats(now: number): MeterStats {
    this.prune(now)
    let lastHour = 0,
      peak = 0
    for (const n of this.buckets.values()) {
      lastHour += n
      peak = Math.max(peak, n)
    }
    return {
      lastMinute: this.recent.length,
      lastHour,
      peakPerMinute: Math.max(peak, this.recent.length),
    }
  }
}

export class RequestMeter {
  private series = new Map<MeterKind, Series>()
  private listeners = new Set<() => void>()
  private revision = 0
  constructor(private readonly now: () => number = Date.now) {}
  record(kind: MeterKind) {
    let s = this.series.get(kind)
    if (!s) {
      s = new Series()
      this.series.set(kind, s)
    }
    s.record(this.now())
    this.revision++
    for (const l of this.listeners) l()
  }
  stats(kind: MeterKind): MeterStats {
    return (
      this.series.get(kind)?.stats(this.now()) ?? { lastMinute: 0, lastHour: 0, peakPerMinute: 0 }
    )
  }
  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => void this.listeners.delete(cb)
  }
  version = () => this.revision
}

/** This tab's meter (one per page). */
export const requestMeter = new RequestMeter()

/**
 * Account-wide rate-limit gate: after the hub answers 429 with `retryAfterMs`, this tab holds
 * further hub requests until then instead of spending invocations on certain refusals. Auth
 * routes are never held (sign-in must stay possible).
 */
export class RateGate {
  until = 0
  constructor(
    private readonly now: () => number = Date.now,
    private readonly maxHoldMs = 60_000,
  ) {}
  hold(retryAfterMs: number) {
    const until = this.now() + Math.min(this.maxHoldMs, Math.max(0, retryAfterMs))
    if (until > this.until) this.until = until
  }
  wait(): number {
    return Math.max(0, this.until - this.now())
  }
}

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>

/** Retry-After (seconds or HTTP date) → ms; undefined when absent or malformed. */
export function retryAfterHeader(res: Response, now = Date.now()): number | undefined {
  const raw = res.headers.get('Retry-After')
  if (!raw) return undefined
  const s = Number(raw)
  if (Number.isFinite(s) && s >= 0) return Math.round(s * 1000)
  const t = Date.parse(raw)
  return Number.isFinite(t) ? Math.max(0, t - now) : undefined
}

/**
 * read-budget: idempotent reads a 429 may silently retry (the hub answered before reading
 * anything; re-sending cannot double-apply). Writes and commands surface the refusal.
 */
const IDEMPOTENT_READ =
  /\/api\/content\/(entity\/(list|read|heads)|history\/(list|read)|report\/(list|read)|get|identity\/read|directory\/read)$/
export const isIdempotentRead = (input: string, init?: RequestInit) =>
  (init?.method ?? 'GET').toUpperCase() === 'GET' || IDEMPOTENT_READ.test(input.split('?')[0]!)
/** Silent retries of one rate-limited read before the refusal reaches the caller. */
export const READ_RETRIES = 3

/**
 * Count every hub HTTP request and honour the rate-limit gate. A rate-limited idempotent read
 * waits out the hub's `retryAfterMs` plus jitter and retries quietly (up to `retries` times), so
 * a short refusal never becomes an error on screen; only a sustained one reaches the caller.
 */
export function meteredFetcher(
  fetcher: Fetcher,
  o: {
    meter?: RequestMeter
    gate?: RateGate
    sleep?: (ms: number) => Promise<void>
    retries?: number
    random?: () => number
  } = {},
): Fetcher {
  const meter = o.meter ?? requestMeter
  const gate = o.gate
  const sleep = o.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const retries = o.retries ?? READ_RETRIES
  const random = o.random ?? Math.random
  return async (input, init) => {
    const held = gate && !/\/api\/auth\//.test(input)
    for (let attempt = 0; ; attempt++) {
      if (held) {
        const wait = gate.wait()
        // Tabs that shared one refusal spread out instead of returning together.
        if (wait > 0) await sleep(wait + Math.round(random() * Math.min(2_000, 250 + wait / 2)))
      }
      meter.record('http')
      const res = await fetcher(input, init)
      if (res.status !== 429 || !gate) return res
      gate.hold(retryAfterHeader(res) ?? 1_000)
      if (!held || attempt >= retries || !isIdempotentRead(input, init)) return res
      await res.body?.cancel().catch(() => {})
    }
  }
}
